/**
 * HospitalBackorderTimeline
 * -------------------------
 * Read-only milestone feed for the hospital dashboard. Pulls
 * `/api/hospital/backorders/{id}/timeline` and renders the Amazon-style
 * fulfillment events posted by admin (Shipped → Delayed → Arrived).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Truck, AlertTriangle, CheckCircle2, MapPin, Clock, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const META = {
  shipped: { label: "Shipped", icon: Truck, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", accent: "bg-emerald-600" },
  delayed: { label: "Delayed", icon: AlertTriangle, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", accent: "bg-amber-500" },
  arrived: { label: "Arrived", icon: CheckCircle2, color: "text-emerald-800", bg: "bg-emerald-100", border: "border-emerald-300", accent: "bg-emerald-700" },
  tracking_update: { label: "Tracking update", icon: MapPin, color: "text-sky-700", bg: "bg-sky-50", border: "border-sky-200", accent: "bg-sky-600" },
};

function fmtTs(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function HospitalBackorderTimeline({ backorderId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/api/hospital/backorders/${backorderId}/timeline`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ milestones: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [backorderId, token]);

  if (loading) {
    return <div className="text-sm text-slate-400 py-3">Loading shipment status…</div>;
  }
  const milestones = data?.milestones || [];
  if (milestones.length === 0) {
    return (
      <div className="text-xs text-slate-400 italic py-2" data-testid="hospital-timeline-empty">
        No fulfillment milestones yet. You'll see live updates here once your order ships.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="hospital-timeline">
      {/* Multi-shipment summary (Feature C) */}
      {(data?.shipments || []).length > 1 && (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2.5" data-testid="hospital-timeline-multi-shipments">
          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 mb-1.5">
            {data.shipments.length} shipments in transit
          </div>
          <div className="space-y-1">
            {data.shipments.map((s, i) => (
              <div key={s.id || i} className="flex items-center justify-between text-xs bg-white rounded border border-emerald-100 px-2 py-1.5">
                <span className="font-bold text-slate-700 w-20">{s.courier}</span>
                <span className="font-mono text-slate-800 flex-1 mx-2 truncate">{s.tracking_number}</span>
                {s.tracking_url && (
                  <a href={s.tracking_url} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-1">
                    Track <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top tracking summary */}
      {data?.tracking_number && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2.5 rounded-md bg-sky-50 border border-sky-200">
          <div className="text-xs text-sky-900">
            <span className="font-bold">{data.courier || "Courier"}</span> ·{" "}
            <span className="font-mono">{data.tracking_number}</span>
            {data.tracking_last_status && (
              <span className="ml-2 text-sky-700">Latest: <strong>{data.tracking_last_status}</strong></span>
            )}
          </div>
          {data.tracking_url && (
            <a
              href={data.tracking_url}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1"
              data-testid="hospital-timeline-track-link"
            >
              Track shipment <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      )}

      <ol className="space-y-1.5">
        {[...milestones].reverse().map((m) => {
          const meta = META[m.type] || META.tracking_update;
          const Icon = meta.icon;
          return (
            <li
              key={m.id || m.ts}
              className={`flex gap-2.5 p-2.5 rounded-md border ${meta.border} ${meta.bg}`}
              data-testid={`hospital-milestone-${m.type}`}
            >
              <div className={`shrink-0 w-7 h-7 rounded-full ${meta.accent} flex items-center justify-center mt-0.5`}>
                <Icon className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-bold ${meta.color}`}>{meta.label}</span>
                  <span className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {fmtTs(m.ts)}
                  </span>
                </div>
                {m.tracking_number && (
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    <span className="font-mono">{m.courier || ""} · {m.tracking_number}</span>
                  </div>
                )}
                {m.estimated_arrival && (
                  <div className="text-[11px] text-amber-700 mt-0.5"><strong>New ETA:</strong> {m.estimated_arrival}</div>
                )}
                {m.reason && (
                  <div className="text-[11px] text-amber-700 mt-0.5"><strong>Reason:</strong> {m.reason}</div>
                )}
                {m.current_tracking_status && (
                  <div className="text-[11px] text-sky-700 mt-0.5"><strong>Status:</strong> {m.current_tracking_status}</div>
                )}
                {m.note && (
                  <div className="text-[11px] text-slate-600 mt-0.5 italic">"{m.note}"</div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
